// Central visual metadata catalog for the notification center. Shared client/server (same
// pattern as trackingStateMachine.ts) -- server.ts imports it to resolve `priority` on
// insert, the frontend uses it for icon/color in the bell and the dedicated page. Presentation
// metadata only: each event's title/message wording still lives inline at its server.ts call
// site (not centralized, see PROJECT_MASTER.md).
//
// The union values below (NotificationType, NotificationPriority) are deliberately left in
// Spanish: they're persisted verbatim as the `tipo`/`prioridad` columns of every existing
// notification row (DB-persisted values, deferred to Fase 4 -- renaming them now would orphan
// every stored notification's type match and require updating every `tipo: '...'` call site
// across server.ts in the same commit).

export type NotificationType =
  | 'nueva_cotizacion'
  | 'cotizacion_aprobada'
  | 'cotizacion_rechazada'
  | 'cotizacion_cambios_solicitados'
  | 'pago_confirmado'
  | 'comprobante_match_sugerido'
  | 'comprobante_recibido'
  | 'comprobante_rechazado'
  | 'guia_anulada'
  | 'alerta_reconciliacion_ups'
  | 'operacion_daily_pendiente'
  | 'bandeja_daily_atascada'
  | 'backup_fallido'
  | 'ingesta_correo_detenida'
  | 'cuenta_bloqueada'
  | 'operacion_pendiente_aval'
  | 'operacion_registrada'
  | 'operacion_aval_aprobado'
  | 'operacion_aval_rechazado'
  | 'correccion_guia_fallida'
  | 'guia_corregida'
  | 'operacion_anulada'
  | 'comision_liquidada'
  | 'guia_lista_para_facturar'
  | 'nota_credito_requerida'
  | 'nota_credito_recibida'
  | 'siigo_sync_fallido'
  | 'ajuste_peso_vencido'
  | 'saldo_favor_generado'
  | 'credito_vencido'
  | 'recoleccion_programada'
  | 'recoleccion_cancelada'
  | 'recoleccion_no_completada'
  | 'lead_whatsapp'
  | 'chat_sin_respuesta'
  | 'caso_operador_creado'
  | 'caso_operador_actualizado'
  | 'caso_operador_aprobado'
  | 'caso_operador_rechazado'
  | 'caso_operador_sla_vencido'
  | 'caso_operador_estancado'
  | 'notificacion_cliente_fallida';

export type NotificationPriority = 'critico' | 'alerta' | 'exito' | 'info';

export interface NotificationTypeMeta {
  priority: NotificationPriority;
  // String name of a lucide-react icon (resolved to a component in notificationIcons.tsx,
  // frontend-only) -- no JSX is imported here so server.ts can use this file without
  // pulling in React.
  iconName: string;
  // Short Tailwind color token (e.g. 'rose' | 'amber' | 'emerald' | 'blue' | 'slate'), the
  // frontend builds bg-*/text-*/border-* from this token.
  colorClass: string;
}

export const NOTIFICATION_CATALOG: Record<NotificationType, NotificationTypeMeta> = {
  // Critical: requires immediate security attention
  cuenta_bloqueada: { priority: 'critico', iconName: 'ShieldAlert', colorClass: 'rose' },
  caso_operador_estancado: { priority: 'critico', iconName: 'AlertOctagon', colorClass: 'rose' },

  // Alert: something went wrong or needs review
  comprobante_rechazado: { priority: 'alerta', iconName: 'FileWarning', colorClass: 'amber' },
  operacion_aval_rechazado: { priority: 'alerta', iconName: 'XCircle', colorClass: 'amber' },
  backup_fallido: { priority: 'alerta', iconName: 'AlertTriangle', colorClass: 'amber' },
  // AUD-043: ingestion has gone too long without completing a cycle. With the pollers' 30-day
  // window, a prolonged silence ends up as a permanent loss of email.
  ingesta_correo_detenida: { priority: 'alerta', iconName: 'AlertTriangle', colorClass: 'amber' },
  correccion_guia_fallida: { priority: 'alerta', iconName: 'AlertTriangle', colorClass: 'amber' },
  // §7.3 MISION_DATOS_Y_SISTEMA.md: a critical milestone (notify the client) that couldn't be
  // met -- no email on file or the mail server rejected it. It used to only sit in a server
  // log nobody watches.
  notificacion_cliente_fallida: { priority: 'alerta', iconName: 'MailWarning', colorClass: 'amber' },
  alerta_reconciliacion_ups: { priority: 'alerta', iconName: 'AlertCircle', colorClass: 'amber' },
  operacion_daily_pendiente: { priority: 'alerta', iconName: 'PackagePlus', colorClass: 'amber' },
  bandeja_daily_atascada: { priority: 'alerta', iconName: 'Clock', colorClass: 'amber' },
  cotizacion_rechazada: { priority: 'alerta', iconName: 'XCircle', colorClass: 'amber' },
  siigo_sync_fallido: { priority: 'alerta', iconName: 'FileWarning', colorClass: 'amber' },
  ajuste_peso_vencido: { priority: 'alerta', iconName: 'AlertCircle', colorClass: 'amber' },
  credito_vencido: { priority: 'alerta', iconName: 'AlertCircle', colorClass: 'amber' },
  caso_operador_rechazado: { priority: 'alerta', iconName: 'XCircle', colorClass: 'amber' },
  caso_operador_sla_vencido: { priority: 'alerta', iconName: 'Clock', colorClass: 'amber' },
  // "We visited your provider and they confirmed they won't deliver the goods" (meeting
  // 11/08): it's the real trigger for a reschedule, not an informational notice -- that's
  // why it comes in as an alert.
  recoleccion_no_completada: { priority: 'alerta', iconName: 'PackageX', colorClass: 'amber' },
  // W-2k: el agente de WhatsApp le pasó un cliente o prospecto; el comercial debe escribirle desde su celular.
  lead_whatsapp: { priority: 'alerta', iconName: 'MessageSquare', colorClass: 'amber' },
  chat_sin_respuesta: { priority: 'alerta', iconName: 'MessageSquare', colorClass: 'amber' },

  // Success: positive confirmation of a business flow
  pago_confirmado: { priority: 'exito', iconName: 'CheckCircle2', colorClass: 'emerald' },
  cotizacion_aprobada: { priority: 'exito', iconName: 'CheckCircle2', colorClass: 'emerald' },
  operacion_aval_aprobado: { priority: 'exito', iconName: 'CheckCircle2', colorClass: 'emerald' },
  comision_liquidada: { priority: 'exito', iconName: 'DollarSign', colorClass: 'emerald' },
  guia_corregida: { priority: 'exito', iconName: 'CheckCircle2', colorClass: 'emerald' },
  caso_operador_aprobado: { priority: 'exito', iconName: 'CheckCircle2', colorClass: 'emerald' },

  // Info: normal operational notice, no urgency
  nueva_cotizacion: { priority: 'info', iconName: 'FileText', colorClass: 'blue' },
  cotizacion_cambios_solicitados: { priority: 'info', iconName: 'MessageSquare', colorClass: 'blue' },
  comprobante_match_sugerido: { priority: 'info', iconName: 'Search', colorClass: 'blue' },
  comprobante_recibido: { priority: 'info', iconName: 'FileUp', colorClass: 'blue' },
  guia_anulada: { priority: 'info', iconName: 'Ban', colorClass: 'slate' },
  operacion_pendiente_aval: { priority: 'info', iconName: 'Clock', colorClass: 'blue' },
  operacion_registrada: { priority: 'info', iconName: 'PackagePlus', colorClass: 'blue' },
  operacion_anulada: { priority: 'info', iconName: 'Ban', colorClass: 'slate' },
  guia_lista_para_facturar: { priority: 'info', iconName: 'FileCheck', colorClass: 'emerald' },
  // The operator overcharged: there's money to recover and a tracking number that can't be invoiced.
  nota_credito_requerida: { priority: 'alerta', iconName: 'AlertTriangle', colorClass: 'amber' },
  nota_credito_recibida: { priority: 'exito', iconName: 'CheckCircle2', colorClass: 'emerald' },
  // The shipment weighed less than quoted: the client overpaid and gets credited.
  saldo_favor_generado: { priority: 'info', iconName: 'PiggyBank', colorClass: 'emerald' },
  recoleccion_programada: { priority: 'exito', iconName: 'CalendarCheck', colorClass: 'emerald' },
  recoleccion_cancelada: { priority: 'info', iconName: 'CalendarX', colorClass: 'slate' },
  caso_operador_creado: { priority: 'info', iconName: 'FolderPlus', colorClass: 'blue' },
  caso_operador_actualizado: { priority: 'info', iconName: 'RefreshCw', colorClass: 'blue' },
};

export const DEFAULT_NOTIFICATION_META: NotificationTypeMeta = {
  priority: 'info',
  iconName: 'Bell',
  colorClass: 'slate',
};

export function getNotificationMeta(type: string | null | undefined): NotificationTypeMeta {
  if (!type) return DEFAULT_NOTIFICATION_META;
  return NOTIFICATION_CATALOG[type as NotificationType] || DEFAULT_NOTIFICATION_META;
}

/**
 * Notification types are lowercase snake_case (the catalog above). The global `sanitizeData`
 * middleware upper-cases any field called `tipo`, so a type read from a query or body has to be
 * folded back before it is compared with what notify() stored (T-19a: the type filter of
 * /notificaciones always came back empty, and a muted type would never be found).
 */
export function normalizeNotificationType(raw: unknown): string {
  return String(raw ?? '').trim().toLowerCase();
}
